import {MegaScreen} from "./MegaScreen.js";
import {
  getIfNotEmpty,
  isEmpty,
  notEmpty,
  isNil,
  notNil,
  cascadingGet
} from '@megaphonenpm/mputils/src/utils.js';
import {Task} from "@megaphonenpm/mputils/src/Task.js";
import {addStyleSheet, getStyleSheet, setCSSRule} from '@megaphonenpm/mputils/src/DOM.js';

/**
 * For all theme objects in the datastore, reads the settings from the theme object
 * and applies the appropriate corresponding CSS rules in the main application stylesheet.
 */

export class ProcessThemeSettingsTask extends Task {
  constructor () {
    super();
    this.name = 'PROCESS_THEME_SETTINGS';
    this.currentThemeCSSRules = null;
    this.currentTheme = null;
  }

  /**
   * Generates CSS rules corresponding to each loaded theme's settings.
   */
  start () {
    super.start();

    MegaScreen.globals.dataStore.themes.forEach(theme => {
      this.currentTheme = theme;
      this.currentThemeCSSRules = new Map();

      // Appy the theme's CSS "inclusions," if any
      ProcessThemeSettingsTask.addCSSInclusions(theme);

      // Fonts
      this.addThemeCSSRule(null, ['*'], new Map([['design.default.screen.font']]));
      this.addThemeCSSRule(null, ['.main_title_txt'], new Map([['design.default.screen.masthead.main_title.font']]));
      this.addThemeCSSRule(null, ['.secondary_title_txt'], new Map([['design.default.screen.masthead.secondary_title.font']]));
      this.addThemeCSSRule(null, ['.ctatitle_txt'], new Map([['design.default.screen.cta.title.font']]));
      this.addThemeCSSRule(null, ['.cta_txt'], new Map([['design.default.screen.cta.text.font']]));
      this.addThemeCSSRule(null, ['.answer_txt'], new Map([['design.products.multichoice_products.screen.default.default.text.default.font']]));
      this.addThemeCSSRule(null, ['.percent_txt'], new Map([['design.products.multichoice_products.screen.default.default.percent.default.font']]));
      this.addThemeCSSRule(null, ['.main_scoreboard_name'], new Map([['design.scoreboards.screen.main_scoreboard.name.font']]));
      this.addThemeCSSRule(null, ['.main_scoreboard_points'], new Map([['design.scoreboards.screen.main_scoreboard.points.font']]));
      this.addThemeCSSRule(null, ['.main_scoreboard_rank'], new Map([['design.scoreboards.screen.main_scoreboard.rank.font']]));
      this.addThemeCSSRule(null, ['.realtime_scoreboard_name_txt'], new Map([['design.scoreboards.screen.realtime_scoreboard.name.font']]));
      this.addThemeCSSRule(null, ['.realtime_scoreboard_points_txt'], new Map([['design.scoreboards.screen.realtime_scoreboard.points.font']]));
      this.addThemeCSSRule(null, ['.realtime_scoreboard_rank_txt'], new Map([['design.scoreboards.screen.realtime_scoreboard.rank.font']]));
      this.addThemeCSSRule(null, ['.headline_txt'], new Map([['design.default.screen.body.headline.font']]));
      this.addThemeCSSRule('.state_end', ['.state_end .headline_txt'], new Map([['design.states.screen.end.body.headline.font']]));
      this.addThemeCSSRule('.state_winner',['.state_winner .headline_txt'], new Map([['design.states.screen.winner.body.headline.font']]));
      this.addThemeCSSRule(null, ['.points_remaining_txt'], new Map([['design.countdowns.scene.screen.default.points.font']]));
      this.addThemeCSSRule(null, ['.time_remaining_txt'], new Map([['design.countdowns.scene.screen.default.time.font']]));
      this.addThemeCSSRule(null, ['.average_vote_txt'], new Map([['design.products.fivestars.screen.average_label.font']]));  // E.g., "3.5" in Five Stars
      this.addThemeCSSRule(null, ['.moodmeter_value_label_txt'], new Map([['design.products.moodmeter.screen.value_label.font']]));  // E.g., "Strongly disagree" in Mood Meter
      this.addThemeCSSRule(null, ['.guessgauge_value_label_txt'], new Map([['design.products.guessgauge.screen.value_label.font']]));  // E.g., "Strongly agree" in Guess Gauge
      this.addThemeCSSRule(null, ['.fivestars_audience_average_txt'], new Map([['design.products.fivestars.screen.result.title.font']]));  // "Audience Average" in Five Stars' result
      this.addThemeCSSRule(null, ['.fivestars_result_txt'], new Map([['design.products.fivestars.screen.result.value_label.font']]));  // Text of final result in Five Stars (e.g., "4.2")
      this.addThemeCSSRule(null, ['.audience_average_txt'], new Map([['design.default.screen.result.title.font']]));  // "Audience Average" in Guess Gauge/Mood Meter's result
      this.addThemeCSSRule(null, ['.averagevote_result_txt'], new Map([['design.default.screen.result.value_label.font']]));  // Text of final result in Guess Gauge/Mood Meter. E.g., "Somewhat agree"
      this.addThemeCSSRule(null, ['.minMaxLabel'], new Map([['design.default.screen.minmax_label.font']]));  // Lowest or highest answer text in Guess Gauge/Mood Meter. E.g., "Terrible!" or "Amazing!"


      // Rundown
      this.addThemeCSSRule(null,['.rundown'], new Map([
        ['design.products.rundown.screen.main.background', {cssPropName: 'background'}],
        ['design.products.rundown.screen.main.font'],
        ['design.products.rundown.screen.main.position.x', {cssPropName: 'left', suffix: 'px'}],
        ['design.products.rundown.screen.main.position.y', {cssPropName: 'top', suffix: 'px'}],
        ['design.products.rundown.screen.main.size.width', {cssPropName: 'width', suffix: 'px'}],
        ['design.products.rundown.screen.main.size.height', {cssPropName: 'height', suffix: 'px'}],
        ['design.products.rundown.screen.main.text_transform', {cssPropName: 'text-transform'}]
          ]));

      this.addThemeCSSRule(null,['.rundown .title'], new Map([
        ['design.products.rundown.screen.title.background', {cssPropName: 'background'}],
        ['design.products.rundown.screen.title.font'],
        ['design.products.rundown.screen.title.padding.bottom', {cssPropName: 'padding-bottom'}],
        ['design.products.rundown.screen.title.padding.left', {cssPropName: 'padding-left'}],
        ['design.products.rundown.screen.title.padding.right', {cssPropName: 'padding-right'}],
        ['design.products.rundown.screen.title.padding.top', {cssPropName: 'padding-top'}],
      ]));

      this.addThemeCSSRule(null,['.rundown .sceneCountdown'], new Map([
        ['design.products.rundown.screen.countdown.background', {cssPropName: 'background'}],
        ['design.products.rundown.screen.countdown.font'],
        ['design.products.rundown.screen.countdown.border.stroke', {cssPropName: 'border'}],
        ['design.products.rundown.screen.countdown.border.bottom', {cssPropName: 'border-bottom'}],
        ['design.products.rundown.screen.countdown.border.left', {cssPropName: 'border-left'}],
        ['design.products.rundown.screen.countdown.border.right', {cssPropName: 'border-right'}],
        ['design.products.rundown.screen.countdown.border.top', {cssPropName: 'border-top'}],
        ['design.products.rundown.screen.countdown.border.radius', {cssPropName: 'border-radius'}],
        ['design.products.rundown.screen.countdown.padding.bottom', {cssPropName: 'padding-bottom'}],
        ['design.products.rundown.screen.countdown.padding.left', {cssPropName: 'padding-left'}],
        ['design.products.rundown.screen.countdown.padding.right', {cssPropName: 'padding-right'}],
        ['design.products.rundown.screen.countdown.padding.top', {cssPropName: 'padding-top'}],
        ['design.products.rundown.screen.countdown.shadow', {cssPropName: 'box-shadow'}]
      ]));

      this.addThemeCSSRule(null,['.rundown .sceneslist li'], new Map([
        ['design.products.rundown.screen.item.default.background', {cssPropName: 'background'}],
        ['design.products.rundown.screen.item.default.border.bottom', {cssPropName: 'border-bottom'}],
        ['design.products.rundown.screen.item.default.border.stroke', {cssPropName: 'border'}],
        ['design.products.rundown.screen.item.default.border.radius', {cssPropName: 'border-radius'}],
        ['design.products.rundown.screen.item.default.font'],
        ['design.products.rundown.screen.item.default.padding.bottom', {cssPropName: 'padding-bottom'}],
        ['design.products.rundown.screen.item.default.padding.left', {cssPropName: 'padding-left'}],
        ['design.products.rundown.screen.item.default.padding.right', {cssPropName: 'padding-right'}],
        ['design.products.rundown.screen.item.default.padding.top', {cssPropName: 'padding-top'}],
        ['design.products.rundown.screen.item.default.shadow', {cssPropName: 'box-shadow'}]
      ]));

      this.addThemeCSSRule(null,['.rundown .sceneslist li.current'], new Map([
        ['design.products.rundown.screen.item.current.background', {cssPropName: 'background'}],
        ['design.products.rundown.screen.item.current.font']
      ]));

      // Answer text correct color
      this.addThemeCSSRule(null,
        ['.AnswerOuterContainer.correct .answer_txt',
         '.AnswerOuterContainer.correct .percent_txt'],
        new Map([['design.products.multichoice_products.screen.default.default.text.correct.color', {cssPropName: 'color'}]]),
        'animation: none !important;');


      // Survey bar correct color
      this.addThemeCSSRule(
        null,
        ['.AnswerOuterContainer.correct .leadingBar',
         '.AnswerOuterContainer.correct .trailingBar',
         '.AnswerOuterContainer.correct .defaultBar',
         '.AnswerOuterContainer.correct .barFG:empty'],
        new Map([
          ['design.products.multichoice_products.screen.default.default.bar.correct.color', {cssPropName: 'background-color'}],
        ]),
        `animation: none !important;`
      );

      // NOTES ON THE PRECEDING CSS IMPLEMENTATION FOR "CORRECT" STYLES:
      // 1) On the use of "animation: none":
      // After an animation plays, in Chrome and Safari its styles cannot be altered, even with !important.
      // Hence, the following code uses "animation: none" to disable animation styles for "leading/trailing" answers.
      // Removing the animations allows "correct/incorrect" styles to be applied.
      //
      // 2) On the use of "!important":
      // In the code exported by GWD for a theme, GWD uses element IDs (e.g., '#percent_txt") to start animations.
      // These ID selectors have a very high CSS specificity. Special effort is, therefore, required to override GWD
      // animation styles. In the preceding code, GWD styles are overridden using the !important keyword, which seems
      // to be the cleanest means of gaining higher specificity than GWD's IDs. For reference, here is an example
      // GWD rule selector that is overriden by "!important" in the preceding code:
      //   #leadingAnswerContainer .gwd-gen-3q4fgwdanimation { ... }
      //
      // 3) On the use of .barFG:empty:
      // When the survey bar foreground (.barFG) has no nested content, its background color acts as
      // the survey-bar color. However, when .barFG contains animations for "trailing" and "leading"
      // survey bars, then those animations provide the survey-bar color, and the .barFG is just a
      // container that should not be colored. (Adding a color would potentially fill empty gaps intentionally
      // added through element padding.) The .barFG:empty CSS selector applies the background-color when
      // .barFG has no nested content only.

      // Guess Gauge pointer color
      this.addThemeCSSRule(null, ['.guessgaugeValuePointer'], new Map([['design.products.guessgauge.screen.valuepointer.color', {cssPropName: 'background'}]]));

      // Insert CSS styles into the document
      this.insertCSSRulesForThemeSettings();
    });

    // Notify listeners that the current task is complete
    this.done();
  }

  /**
   * For the specified theme setting, queue a CSS rule to be added to the main stylesheet.
   *
   * @param selectorPrefix A partial selector, which is prepended to each selector in the list of selectors. Allows for
   *                       the applicationRoot div's current state and/or current product class to be included in the final selector.
   * @param selectors An array of the CSS selectors for the given setting. E.g., ['.AnswerOuterContainer.correct .deafaultBar', ...].
   *                  The theme's CSS prefix is added automatically to each selector in the list.
   * @param settingsMap A Map whose keys are theme settings paths and values are objects of the format: {cssPropName: '...', suffix: '...'}.
   *                    Example:
   *                    new Map ([['design.products.multichoice_products.screen.default.default.bar.correct.color', {cssPropName: 'background-color'}]])
   * @param additionalCSSProperties A string containing properties to add after the settings properties in the CSS rule.
   *
   * Example:
   * // This addThemeCSSRule() call...
   * this.addThemeCSSRule(null,['.rundown'], new Map([
   *    ['design.products.rundown.screen.main.background', {cssPropName: 'background'}],
   *    ['design.products.rundown.screen.main.font']
   *  ]));
   *
   * // ...creates the following CSS rule, which is added to the 'main' stylesheet:
   * .applicationRoot.current_screen_theme_fieldname_ColinsTestingTheme.gwd.theme_settings .rundown {
   *   background: linear-gradient(309deg, rgb(255, 255, 255) 0%, rgb(193, 193, 193) 100%);
   *   color: rgb(0, 0, 255);
   *   font-size: 32px;
   * }
   */
   addThemeCSSRule (selectorPrefix = '', selectors = null, settingsMap = null, additionalCSSProperties = '') {
    if (notEmpty(selectors)) {
      // Add theme prefix to all selectors
      let selectorBase = `${MegaScreen.globals.themeService.getCSSPrefixForTheme(this.currentTheme.id)}.theme_settings `;
      selectors = selectors.map(selector => `${isNil(selectorPrefix) ? '' : selectorPrefix} ${selectorBase} ${selector}`);

      // Create CSS properties string (to be added to the final rule)
      let cssPropertiesString = '';
      settingsMap.forEach((cssPropertyInfo, themeSettingPath) => {
        let settingValue = getIfNotEmpty(this.currentTheme, themeSettingPath, '');
        let settingSuffix = getIfNotEmpty(cssPropertyInfo, 'suffix', '');
        if (notEmpty(settingValue)) {
          if (themeSettingPath.endsWith('font')) {
            // Special case to convert .font setting children to CSS
            let fontPropertiesString = ProcessThemeSettingsTask.mapThemeFontSettingToCSSProperties(settingValue);
            if (notNil(fontPropertiesString)) {
              cssPropertiesString += fontPropertiesString;
            }
          } else {
            cssPropertiesString += `${cssPropertyInfo.cssPropName}: ${settingValue}${settingSuffix};`;
          }
        }
      });

      // Queue the selectors and properties for addition to the main stylesheet as CSS rule
      if (notEmpty(cssPropertiesString)) {
        cssPropertiesString += isEmpty(additionalCSSProperties) ? '' : `;${additionalCSSProperties}`;
        this.currentThemeCSSRules.set(selectors.join(', '), cssPropertiesString);
      }
    }
  }

  /**
   * Inserts the current theme's queued CSS rules into the document.
   */
  insertCSSRulesForThemeSettings () {
    let mainStylesheet = getStyleSheet('main');

    this.currentThemeCSSRules.forEach((cssProperties, cssSelector) => {
      // Retrieve this theme setting's value
      if (notNil(cssProperties)) {
        setCSSRule(mainStylesheet, `${cssSelector}`, cssProperties);
      }
    });
  }
}

//==================================================================================================
// STATIC METHODS
//==================================================================================================

/**
 * A property-mapping function for use with addThemeCSSRule(). Given a standard
 * theme-settings "font" node, returns the corresponding CSS properties, ready
 * for insertion into a stylesheet rule.
 *
 * For example, given:
 * design.products.fivestars.screen.result.title.font
 *
 * Returns:
 * "color: #FF0000; family: Arial; font-size: 14px; font-weight: 700"
 *
 * @param fontSettingValue
 * @returns {*}
 */
ProcessThemeSettingsTask.mapThemeFontSettingToCSSProperties = (fontSettingValue) => {
  let properties = '';
  let propertiesMap = new Map();
  propertiesMap.set('color',  {name: 'color', suffix: ''});
  propertiesMap.set('family', {name: 'font-family', suffix: ''});
  propertiesMap.set('size',   {name: 'font-size', suffix: 'px'});
  propertiesMap.set('weight', {name: 'font-weight', suffix: ''});

  propertiesMap.forEach((cssProperty, themeSettingName) => {
    let settingValue = getIfNotEmpty(fontSettingValue, themeSettingName);
    if (notNil(settingValue)) {
      properties += `${cssProperty.name}: ${settingValue}${cssProperty.suffix}; `;
    }
  });

  return properties === '' ? null : properties;
};

/**
 * Adds raw CSS "inclusions" from the specified theme. Inclusions are arbitrary CSS source code that
 * can be added to any theme within Theme Designer. The theme-settings property for CSS inclusions
 * resides at:
 *
 *   design.CSS.screen
 *
 * Note that selectors in CSS-inclusion source code do not have a theme id prefix. For example, a
 * rule for "main title text" would be written as:
 *
 *   .main_title_txt {
 *     background: #ff0000;
 *   }
 *
 * To restrict each theme's CSS inclusions to each theme, the addCSSInclusions() function automatically
 * adds a selector prefix to all selectors found in CSS inclusions. For exampple, in the Network theme,
 * in the final <style> element appended to the document in MegaScreen, the .main_title_txt rule above
 * would become:
 *
 *   .current_screen_theme_fieldname_network .main_title_txt {
 *     background-color: rgb(255, 0, 0);
 *   }
 *
 * In the above example, .current_screen_theme_fieldname_network would match the class of the same name
 * automatically added to the applicationRoot <div> whenever the current theme in MegaScreen is Network.
 *
 * Note that MegaScreen attaches several application-state CSS classes to the .applicationRoot <div>.
 * For example, during a question with three answers, MegaScreen adds the CSS class
 * .current_numanswers_3 to the .applicationRoot <div>. To allow CSS inclusions to select elements
 * using those state-specific classes, addCSSInclusions() chains the theme-prefix class before the
 * state-specific class (so that both classes must be present on applicationRoot to trigger the style).
 *
 * For example, the following CSS Inclusion (as written in Theme Designer) changes .headline_txt's
 * color to red, but only during Tweet to TV scenes:
 *
 *   .current_screen_product_tweettotv .headline_txt {
 *     color: #ff0000;
 *   }
 *
 * To ensure that the preceding style is applied to the correct element, addCSSInclusions() inserts
 * the theme prefix immediately before .current_screen_product_tweettotv, so, for the Network theme,
 * the final CSS rule injected into the DOM is:
 *
 *   .current_screen_theme_fieldname_network.current_screen_product_tweettotv .headline_txt {
 *     color: #ff0000;
 *   }
 *
 */
ProcessThemeSettingsTask.addCSSInclusions = theme => {
  // Get CSS inclusions for the specified theme
  let cssInclusions = getIfNotEmpty(theme, 'design.CSS.screen');

  if (notEmpty(cssInclusions)) {
    // Prepare style sheets and selector prefix
    let tempStyleElement = addStyleSheet('tempStyleSheet');
    let finalStyleElement = addStyleSheet(`cssInclusions${theme.field_name}_${theme.id}`);
    let selectorThemePrefix = MegaScreen.globals.themeService.getCSSPrefixForTheme(theme.id);
    let prefixedCSSInclusions = '';  // The final CSS to be included in the document

    // Special "state classes" that are applied by MegaScreen to the .applicationRoot div
    let applicationRootStateClasses = ['.current_screen_product_',
      '.current_screen_productgroup_',
      '.current_numanswers_',
      '.quiz',
      '.hide_votes',
      '.show_votes'];

    // Temporarily add the original "as written" styles to the document so that they can be processed
    // with JavaScript.
    tempStyleElement.textContent = cssInclusions;
    let originalRules = (tempStyleElement.sheet.cssRules);

    // Add selectorThemePrefix to all selectors of all rules found in the theme's original CSS inclusions
    for (let i = 0, numRules = originalRules.length; i < numRules; i++) {
      // Get the complete CSS source code for this rule
      let ruleCSSSource = originalRules[i].cssText;

      // If this is an "at rule," such as @keyframes or @font-face, then allow it to be added to the
      // document without alteration (i.e., without adding any theme prefix). This approach creates
      // global "at rules," which risks collisions between themes (e.g., if two themes contain an
      // @keyframes rule with the same name, the second theme's rule will override the first theme's
      // rule). Ideally, MegaScreen should add prefixes to @keyframes rule names, but doing
      // so would require finding and replacing all references to those keyframes (e.g., in animation:
      // declarations), which, itself, would be complex and error prone. Therefore, global "at rules"
      // are tolerated without prefixes for now.
      if (ruleCSSSource.trimStart().startsWith('@')) {
        prefixedCSSInclusions += `${ruleCSSSource}\n`;
        continue;
      }

      // Retrieve selector text and style properties for the rule currently being processed
      let originalRuleSelectorText = originalRules[i].selectorText;
      let originalRuleStyles = originalRules[i].style.cssText;
      let prefixedRuleSelectorText;

      if (notEmpty(originalRuleSelectorText)) {
        // Retrieve this rule's selectors so that they can be prefixed with the selectorThemePrefix
        let selectors = originalRuleSelectorText.split(',');

        // Add the selectorThemePrefix at the appropriate location
        prefixedRuleSelectorText = selectors.map(selector => {
          for (let applicationRootStateClass of applicationRootStateClasses) {
            if (selector.includes(applicationRootStateClass)) {
              // This selector contains at least one of the special "applicationRootStateClasses", so chain
              // the selectorThemePrefix to the state class
              return selector.split(applicationRootStateClass).join(`${selectorThemePrefix}${applicationRootStateClass}`);
            }
          }
          // This selector does not contain any special "applicationRootStateClasses", so add the
          // selectorThemePrefix as an ancestor of the original selector.
          return `${selectorThemePrefix} ${selector}`;
        })
        .join(', '); // Concatenate all selectors for this rule
      }

      // Add this rule, with freshly prefixed selectors, to the CSS to be added to the document
      prefixedCSSInclusions += `${prefixedRuleSelectorText} { ${originalRuleStyles} }\n`;
    }

    // Add the prefixed theme CSS to the document
    finalStyleElement.textContent = prefixedCSSInclusions;

    // Remove the original "as written" styles
    tempStyleElement.remove();
  }
};
